import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Login from "./../views/auth/Login";
import SidebarLayout from "./../components/SidebarLayout";
import { PrivateRoute } from "./PrivateRoute";
import { useDispatch, useSelector } from "react-redux";
import { auth } from "../../firebase/firebase-config";
import {
  logoutUser,
  setIsLoadingAuth,
  signInWithEmail,
} from "../store/actions/auth";
import { CircularProgress, Box } from "@mui/material";

import Register from "../views/auth/Register";

const HomeView = lazy(() => import("../views/HomeView").then(m => ({ default: m.HomeView })));
const ProfileView = lazy(() => import("../views/profile/ProfileView").then(m => ({ default: m.ProfileView })));
const AcademyView = lazy(() => import("../views/academy/AcademyView").then(m => ({ default: m.AcademyView })));
const TradingView = lazy(() => import("../views/academy/TradingView").then(m => ({ default: m.TradingView })));
const PlaylistView = lazy(() => import("../views/academy/PlaylistView").then(m => ({ default: m.PlaylistView })));
const PlaylistTradingView = lazy(() => import("../views/academy/PlaylistTradingView").then(m => ({ default: m.PlaylistTradingView })));
const SyntheticCategoryView = lazy(() => import("../views/academy/SyntheticCategoryView").then(m => ({ default: m.SyntheticCategoryView })));
const VideoPlayer = lazy(() => import("../views/academy/VideoPlayer").then(m => ({ default: m.VideoPlayer })));
const ScheduleView = lazy(() => import("../views/schedules/ScheduleView").then(m => ({ default: m.ScheduleView })));

const Spinner = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <CircularProgress size={36} />
  </Box>
);

function AppRoutes() {
  const dispatch = useDispatch();
  const history = useLocation();
  const { loggedIn } = useSelector((state) => state.auth);
  const lastUrl = localStorage.getItem("lastUrl") || "/home";
  const [isAuthResolved, setIsAuthResolved] = useState(false);

  useEffect(() => {
    const handleAuthStateChange = async (authUser) => {
      dispatch(setIsLoadingAuth(true));

      if (authUser) {
        try {
          const metadata = await authUser.getIdTokenResult();
          dispatch(signInWithEmail(authUser.toJSON(), metadata.claims));
          sessionStorage.setItem("user", JSON.stringify(authUser.toJSON()));
        } catch (error) {
          // ignore
        }
      } else {
        sessionStorage.removeItem("user");
        dispatch(logoutUser());
      }

      dispatch(setIsLoadingAuth(false));
      setIsAuthResolved(true);
    };

    const unsubscribe = auth.onAuthStateChanged(handleAuthStateChange);
    return () => unsubscribe();
  }, [dispatch]);

  if (!isAuthResolved) {
    return <Spinner />;
  }

  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={<SidebarLayout />}>
          <Route
            path=""
            element={
              loggedIn ? (
                <Navigate replace to={lastUrl} />
              ) : (
                <Navigate to="/auth/login" />
              )
            }
          />
          <Route
            path="/*"
            element={
              <PrivateRoute isLoggedIn={loggedIn}>
                <Routes>
                  <Route
                    path="/auth/login"
                    element={
                      <Navigate
                        replace
                        to={lastUrl === "/auth/login" ? "/home" : lastUrl}
                      />
                    }
                  />
                  <Route index path="/home" element={<HomeView />} />
                  <Route path="/home" element={<HomeView />} />
                  <Route path="/profile" element={<ProfileView />} />
                  <Route path="/academy" element={<AcademyView />} />
                  <Route path="/trading" element={<TradingView />} />
                  <Route path="/videoplayer" element={<VideoPlayer />} />
                  <Route path="/academy/playlist/:category" element={<PlaylistView />} />
                  <Route path="/trading/playlist/:category" element={<PlaylistTradingView />} />
                  <Route path="/trading/synthetics" element={<SyntheticCategoryView />} />
                  <Route path="/schedules" element={<ScheduleView />} />
                </Routes>
              </PrivateRoute>
            }
          />
        </Route>

        {!loggedIn ? (
          <Route Route path="/auth">
            <Route path="" element={<Navigate replace to="login" />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
          </Route>
        ) : null}
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
